import { singleOrArrayToArray } from '@dirootie-extensions/javascript';
import { IKeyedObject, SingleOrArray } from '@dirootie-extensions/typescript';
import _ from 'lodash';
import { ArrayWithObjectKeys } from './arrays.types';

/**
 * Initialize a 1D array with size and predifined value.
 *
 * @param {number} size of the 1D array -
 * @param {value} value initial value to put in each position
 * @return ]{any[} Return initialized array with all positions filled with initial value
 */
const initialize1D = (size: number, value: any) => {
  return Array.from({ length: size }, () => value);
};

/**
 * Initialize a 2D array with size and predifined value. It is a Square matrix m*n = size*size. Same number of rows and columns.
 *
 * @param {number} size of the 2D array -
 * @param {value} value initial value to put in each cell
 * @return {any[][]} Return initialized square array with all cells filled with initial value
 */
const initialize2D = (size: number, value: any) => {
  return Array.from({ length: size }, () => initialize1D(size, value));
};

/**
 * Converts an array of values to an object using the properties passed in the second array, by position.
 * The value in positional array deternmines that the value same position in first array should became the
 *
 * @param {any[]} arr of values to be converted to object properties by position -
 * @param {ArrayWithObjectKeys<T>} positionMap is a string array where all string must be properties of the resulting object. Position (array index) determines the that the value on the same position of first array should became the property using the value of the position.
 * @return {T} Return object with the properties mapped from the array to the object
 */
const toObjectByProps = <T>(
  arr: any[],
  positionMap: ArrayWithObjectKeys<T>
): T => {
  if (_.isArray(arr)) {
    const result = (arr as any[]).reduce((prev, curr, i) => {
      const propName = positionMap[i];
      return {
        [propName]: curr,
        ...prev,
      };
    }, {});
    return result as T;
  }
  throw new Error('provided input is not an array');
};

/**
 * Converts array with objects to an object. Uses the first existing key, passed in the second parameter, as the key of the object in the array in the result object.
 * @param {any[]} arr - The array to convert
 * @param {SingleOrArray<string>} keys The property name to use as a key when converting the element to object.
 * @returns {T} Returns the created object
 */
const arrayToObjectWithKey = <T, TR extends IKeyedObject<T>>(
  arr: T[],
  keys?: SingleOrArray<keyof T>
): TR => {
  var resultKeys = singleOrArrayToArray(keys);
  var resultAsObj = arr.reduce((accumulator, el) => {
    var key =
      keys !== undefined
        ? resultKeys.map((x) => el[x]).filter((y) => y !== undefined)[0]
        : el;
    return { ...accumulator, [key as string]: el };
  }, {});
  return resultAsObj as TR;
};

type ObjectOrFunction = {
  <T, TR extends 'obj' | 'func'>(
    arr: T[],
    key: string | number,
    output?: TR
  ): TR extends 'obj' ? IKeyedObject<number> : (val: string) => T;
  <T, TR extends 'obj' | 'func'>(
    arr: T[],
    key: string | number,
    output: TR
  ): void;
};

const toObjectWithKeyPosition: ObjectOrFunction = (arr, key, output) => {
  const mapper: IKeyedObject<number> = {};
  arr.forEach((x: any, i) => {
    const keyValue =
      _.isString(x) || _.isNumber(x)
        ? x
        : typeof key === 'number'
        ? x[key as number]
        : (x as any)[key as string];
    mapper[keyValue as string] = i;
  });
  if (output === 'obj') {
    return mapper;
  } else {
    return (val: string) => arr[mapper[val]];
  }
};

export interface ArrayUtils {
  initialize1D: (size: number, vlalue: any) => any[];
  initialize2D: (size: number, value: any) => any[][];
  toObjectByProps: typeof toObjectByProps;
  toObjectWithKey: typeof arrayToObjectWithKey;
  toObjectWithKeyPosition: typeof toObjectWithKeyPosition;
}

const internalArrayUtils = (): ArrayUtils => {
  return {
    initialize1D,
    initialize2D,
    toObjectByProps,
    toObjectWithKey: arrayToObjectWithKey,
    toObjectWithKeyPosition,
  };
};

export const arrayUtils = internalArrayUtils();
